// NYC neighborhood/region → ZIP code groupings used by the phleb portal to
// filter draws by area. Borough-wide ZIPs come from the USPS / NYC OpenData
// MODZCTA list; region boundaries follow the common-knowledge groupings
// (NYC DOHMH "Modified ZCTA Neighborhoods", with light grouping for usability).

const NYC_REGIONS = {
  Manhattan: {
    'Downtown / FiDi':         ['10004','10005','10006','10007','10038','10280','10282'],
    'Tribeca / SoHo':          ['10012','10013','10014'],
    'Lower East Side':         ['10002','10003','10009'],
    'Greenwich Village':       ['10011','10003','10014'],
    'Chelsea / Midtown West':  ['10001','10018','10019','10036'],
    'Midtown East':            ['10016','10017','10022'],
    'Upper East Side':         ['10021','10028','10044','10065','10075','10128'],
    'Upper West Side':         ['10023','10024','10025','10069'],
    'Harlem':                  ['10026','10027','10029','10030','10037','10039'],
    'Washington Heights / Inwood': ['10031','10032','10033','10034','10040'],
  },
  Brooklyn: {
    'Downtown / Brownstone (Heights, Cobble Hill, Boerum Hill)':
      ['11201','11217','11231'],
    'North Brooklyn (Williamsburg, Greenpoint)':
      ['11206','11211','11222','11249'],
    'Bushwick / Ridgewood Edge':
      ['11221','11237','11207'],
    'Bed-Stuy':
      ['11205','11216','11221','11233','11238'],
    'Crown Heights / Prospect Heights':
      ['11213','11216','11217','11225','11238'],
    'Park Slope / Gowanus':
      ['11215','11217','11231','11232'],
    'Sunset Park / Bay Ridge / Dyker':
      ['11209','11220','11228','11232'],
    'Bensonhurst / Bath Beach':
      ['11214','11219','11223','11228'],
    'Coney Island / Brighton Beach / Sheepshead Bay':
      ['11223','11224','11229','11235'],
    'Flatbush / Midwood / Kensington':
      ['11218','11226','11230','11210'],
    'Canarsie / Mill Basin / Marine Park':
      ['11234','11236','11239'],
    'East New York / Brownsville':
      ['11207','11208','11212','11233','11236'],
  },
  Queens: {
    'Astoria / Long Island City':
      ['11101','11102','11103','11105','11106'],
    'Sunnyside / Woodside / Jackson Heights':
      ['11104','11372','11377','11378'],
    'Elmhurst / Corona':
      ['11368','11369','11373'],
    'Flushing / College Point':
      ['11354','11355','11356','11357'],
    'Forest Hills / Rego Park / Kew Gardens':
      ['11374','11375','11385','11415'],
    'Jamaica / South Jamaica':
      ['11432','11433','11434','11435','11436'],
    'Far Rockaway / Rockaway Beach':
      ['11691','11692','11693','11694','11697'],
    'Bayside / Whitestone / Fresh Meadows':
      ['11358','11360','11361','11362','11363','11364','11365','11366','11367'],
    'Glendale / Maspeth / Middle Village':
      ['11378','11379','11385'],
    'Howard Beach / Ozone Park / Richmond Hill':
      ['11414','11416','11417','11418','11419','11420','11421','11422','11423'],
  },
  Bronx: {
    'South Bronx / Mott Haven / Hunts Point':
      ['10451','10454','10455','10459','10474'],
    'Concourse / Highbridge':
      ['10452','10456','10457'],
    'Fordham / University Heights':
      ['10453','10458','10468'],
    'Tremont / West Farms':
      ['10457','10460'],
    'Riverdale / Kingsbridge':
      ['10463','10471'],
    'Norwood / Bedford Park':
      ['10458','10467','10468'],
    'Pelham Bay / Throgs Neck / Co-op City':
      ['10461','10462','10464','10465','10472','10473','10475'],
    'Eastchester / Wakefield / Williamsbridge':
      ['10466','10467','10469','10470'],
  },
  'Staten Island': {
    'North Shore (St. George, Tompkinsville, Stapleton)':
      ['10301','10302','10303','10304','10310'],
    'Mid-Island (New Springville, Heartland Village)':
      ['10314'],
    'South Shore (Great Kills, Tottenville)':
      ['10306','10307','10308','10309','10312'],
    'West Shore (Bloomfield, Mariners Harbor)':
      ['10303','10311'],
    'East Shore (Midland Beach, Dongan Hills)':
      ['10304','10305','10306'],
  },
};

// Borough → flat list of all its ZIPs (deduped).
export const NYC_BOROUGH_ZIPS = Object.fromEntries(
  Object.entries(NYC_REGIONS).map(([borough, regions]) => [
    borough,
    Array.from(new Set(Object.values(regions).flat())).sort(),
  ]),
);

// "Borough — Region" → array of ZIPs. Stable keys for the picker UI.
export const NYC_REGION_CHOICES = Object.entries(NYC_REGIONS).flatMap(([borough, regions]) =>
  Object.entries(regions).map(([region, zips]) => ({
    key: `${borough} — ${region}`,
    borough,
    region,
    zips: Array.from(new Set(zips)).sort(),
  })),
);

// Reverse map ZIP → list of region labels it's in, for badge rendering.
const ZIP_TO_REGION_LABELS = (() => {
  const m = new Map();
  for (const choice of NYC_REGION_CHOICES) {
    for (const zip of choice.zips) {
      if (!m.has(zip)) m.set(zip, []);
      m.get(zip).push(choice.region);
    }
  }
  return m;
})();

export function getRegionsForZip(zip) {
  return ZIP_TO_REGION_LABELS.get(String(zip || '')) || [];
}

export default NYC_REGIONS;
